import { makeAutoObservable, runInAction } from 'mobx';
import { HttpClient } from '@/api/httpClient';
import type {
  VerifyResponse,
  User,
  PlanItem,
  SubscriptionStatus,
  SubscriptionStatusResponse,
  PaymentMethodSelectResponse,
  SubscriptionInitResponse,
  SubscriptionConfirmResponse,
  HandoffResponse,
  LinkDecodeResponse,
  ActionTokenAction,
  ActionTokenResponse,
  SessionCodeConsumeResponse,
  PaymentAnalyticsEventCreateRequest,
  PaymentAnalyticsEventCreateResponse,
} from '@/types/api';

const AUTH_ACCESS_TOKEN_KEY = 'auth_access_token';
const AUTH_REFRESH_TOKEN_KEY = 'auth_refresh_token';
const CONFIRMED_CANCELLATION_STATUSES = new Set([
  'canceled',
  'incomplete_expired',
]);

export class ApiStore {
  accessToken: string | null = null;
  refreshToken: string | null = null;
  user: User | null = null;
  private httpClient: HttpClient;

  constructor() {
    makeAutoObservable(this);

    // Initialize from localStorage
    if (typeof window !== 'undefined') {
      this.accessToken = localStorage.getItem(AUTH_ACCESS_TOKEN_KEY);
      this.refreshToken = localStorage.getItem(AUTH_REFRESH_TOKEN_KEY);
    }

    // Create HTTP client
    this.httpClient = new HttpClient(
      () => this.accessToken,
      () => this.refreshToken,
      (accessToken, refreshToken) => {
        runInAction(() => {
          this.accessToken = accessToken;
          this.refreshToken = refreshToken;
        });
      },
      () => {
        runInAction(() => {
          this.logout();
        });
      },
    );
  }

  get isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  private saveTokens(): void {
    if (typeof window !== 'undefined') {
      if (this.accessToken) {
        localStorage.setItem(AUTH_ACCESS_TOKEN_KEY, this.accessToken);
      }
      else {
        localStorage.removeItem(AUTH_ACCESS_TOKEN_KEY);
      }

      if (this.refreshToken) {
        localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, this.refreshToken);
      }
      else {
        localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
      }
    }
  }

  syncFromStorage(): void {
    if (typeof window !== 'undefined') {
      const storedAccess = localStorage.getItem(AUTH_ACCESS_TOKEN_KEY);
      const storedRefresh = localStorage.getItem(AUTH_REFRESH_TOKEN_KEY);

      if (storedAccess && !this.accessToken) {
        this.accessToken = storedAccess;
      }
      if (storedRefresh && !this.refreshToken) {
        this.refreshToken = storedRefresh;
      }
    }
  }

  // Auth methods
  async getMe(): Promise<User | null> {
    try {
      const userData = await this.httpClient.get<User>('/v1/auth/me');
      runInAction(() => {
        this.user = userData;
      });
      return userData;
    }
    catch (error) {
      this.handleCriticalAuthError(error, 'getMe');
      return null;
    }
  }

  async requestCode(email: string): Promise<{ ok: boolean; result?: string }> {
    try {
      const result = await this.httpClient.post<string>('/v1/auth/request-code', { email });
      return { ok: true, result };
    }
    catch {
      return { ok: false };
    }
  }

  async verifyCode(email: string, code: string): Promise<VerifyResponse> {
    const res = await this.httpClient.post<VerifyResponse>('/v1/auth/verify-code', {
      email,
      code,
    });

    runInAction(() => {
      this.accessToken = res.accessToken;
      this.refreshToken = res.refreshToken;
      this.saveTokens();
    });

    // Fetch user profile after successful authentication
    await this.getMe();
    await this.getSubscriptionStatus();

    return res;
  }

  async loginWithHandoff(code: string): Promise<VerifyResponse> {
    const res = await this.httpClient.post<VerifyResponse>('/v1/auth/handoff/consume', {
      code,
    });

    runInAction(() => {
      this.accessToken = res.accessToken;
      this.refreshToken = res.refreshToken;
      this.saveTokens();
    });

    // Fetch user profile after successful authentication
    await this.getMe();
    await this.getSubscriptionStatus();

    return res;
  }

  async consumeSession(code: string): Promise<SessionCodeConsumeResponse> {
    const res = await this.httpClient.post<SessionCodeConsumeResponse>(
      '/v1/auth/session/consume',
      { code },
    );

    runInAction(() => {
      this.user = null;
      this.accessToken = res.accessToken;
      this.refreshToken = res.refreshToken;
      this.saveTokens();
    });

    return res;
  }

  logout(): void {
    this.accessToken = null;
    this.refreshToken = null;
    this.user = null;
    this.saveTokens();
  }

  private handleCriticalAuthError(error: unknown, context: string): void {
    console.error(`[api-store] Critical auth error in ${context}:`, error);

    let isAuthError = false;

    if (error && typeof error === 'object') {
      // Check status from response
      if ('status' in error) {
        const status = Number(error.status);
        isAuthError = status === 401 || status === 403;
      }
      // Check response.status if exists (axios error)
      else if ('response' in error && error.response && typeof error.response === 'object' && 'status' in error.response) {
        const status = Number((error.response as Record<string, unknown>).status);
        isAuthError = status === 401 || status === 403;
      }
    }

    // Fallback to string check
    if (!isAuthError) {
      const errorStr = String(error);
      isAuthError
        = errorStr.includes('401')
          || errorStr.includes('403')
          || errorStr.includes('Forbidden')
          || errorStr.includes('Unauthorized');
    }

    if (isAuthError) {
      this.logout();
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
  }

  // Public methods
  async getPlans(): Promise<{ items: PlanItem[] }> {
    try {
      return await this.httpClient.get<{ items: PlanItem[] }>('/v1/subscription/plans');
    }
    catch (error) {
      console.error('getPlans error:', error);
      return { items: [] };
    }
  }

  async createCustomer(payload: { email: string }): Promise<VerifyResponse | null> {
    try {
      const res = await this.httpClient.post<VerifyResponse>('/v1/signup/customer/initial', payload);

      runInAction(() => {
        this.accessToken = res.accessToken;
        this.refreshToken = res.refreshToken;
        this.saveTokens();
      });

      // Fetch user profile after successful customer creation
      await this.getMe();
      await this.getSubscriptionStatus();

      return res;
    }
    catch (error) {
      console.error('createCustomer error:', error);
      return null;
    }
  }

  // Subscription methods
  async createSubscription(payload: {
    planId: string;
    methodData: { paymentMethodId: string };
  }): Promise<SubscriptionStatusResponse | null> {
    try {
      return await this.httpClient.post<SubscriptionStatusResponse>(
        '/v1/subscription/create',
        payload,
      );
    }
    catch (error) {
      this.handleCriticalAuthError(error, 'createSubscription');
      return null;
    }
  }

  async activateSessionSubscription(payload: {
    planId: string;
  }): Promise<SubscriptionStatus> {
    try {
      return await this.httpClient.post<SubscriptionStatus>(
        '/v1/subscription/session/activate',
        payload,
      );
    }
    catch (error) {
      this.handleCriticalAuthError(error, 'activateSessionSubscription');
      throw error;
    }
  }

  async getSubscriptionStatus(): Promise<SubscriptionStatusResponse | null> {
    try {
      return await this.httpClient.get<SubscriptionStatusResponse>('/v1/subscription/status');
    }
    catch (error: unknown) {
      // Handle 204 No Content (no subscription)
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 204) {
        console.log('getSubscriptionStatus: No subscription found (204)');
        return null;
      }
      console.error('getSubscriptionStatus error:', error);
      this.handleCriticalAuthError(error, 'getSubscriptionStatus');
      return null;
    }
  }

  async cancelSubscription(): Promise<SubscriptionStatus> {
    try {
      const subscription = await this.httpClient.post<SubscriptionStatus>(
        '/v1/subscription/cancel',
      );

      if (
        !subscription
        || !CONFIRMED_CANCELLATION_STATUSES.has(subscription.status)
      ) {
        throw new Error('Subscription cancellation was not confirmed');
      }

      return subscription;
    }
    catch (error) {
      this.handleCriticalAuthError(error, 'cancelSubscription');
      throw error;
    }
  }

  async getSubscriptionHistory(): Promise<{ items: Array<Record<string, unknown>> }> {
    try {
      return await this.httpClient.get<{ items: Array<Record<string, unknown>> }>(
        '/v1/subscription/history',
      );
    }
    catch (error) {
      console.error('getSubscriptionHistory error:', error);
      return { items: [] };
    }
  }

  // Payment method
  async initPaymentMethodSelect(): Promise<PaymentMethodSelectResponse> {
    try {
      return await this.httpClient.get<PaymentMethodSelectResponse>('/v1/payment-method/select');
    }
    catch {
      return { setup: { clientToken: '', sessionToken: '' } };
    }
  }

  // Support
  async submitFeedback(text: string): Promise<{ ok: boolean; result?: string }> {
    try {
      const result = await this.httpClient.post<string>('/v1/support/feedback', { text });
      return { ok: true, result };
    }
    catch (err) {
      console.error('submitFeedback error:', err);
      return { ok: false };
    }
  }

  // WebView Handoff
  async createHandoff(): Promise<{ ok: boolean; token?: string }> {
    try {
      const result = await this.httpClient.post<HandoffResponse>('/v1/auth/handoff/create');
      return { ok: true, token: result.code };
    }
    catch (error) {
      console.error('createHandoff error:', error);
      return { ok: false };
    }
  }

  // Utils
  async decodeLink(code: string): Promise<{ ok: boolean; data?: string }> {
    try {
      console.log('Decoding link with code:', code);
      const result = await this.httpClient.post<LinkDecodeResponse>('/v1/utils/link/decode', { code });
      console.log('Link decoded successfully:', result);
      return { ok: true, data: result.data };
    }
    catch (error) {
      console.error('decodeLink error:', error);
      return { ok: false };
    }
  }

  // Action token for signup/conversion requests
  async getActionToken(action: ActionTokenAction): Promise<string> {
    const res = await this.httpClient.get<ActionTokenResponse>(
      '/v1/auth/actions/token',
      { action },
    );
    return res.token;
  }

  // Signup Subscription Flow
  async initSubscription(payload?: { planId?: string }): Promise<SubscriptionInitResponse> {
    try {
      const token = await this.getActionToken('signup.subscription.initial');
      const requestBody = payload && payload.planId ? payload : {};
      return await this.httpClient.post<SubscriptionInitResponse>(
        '/v1/signup/subscription/initial',
        requestBody,
        { headers: { 'X-Action-Token': token } },
      );
    }
    catch (error) {
      console.error('initSubscription error:', error);
      throw error;
    }
  }

  // Conversion click (requires action token)
  async conversionClick(payload?: Record<string, unknown>): Promise<void> {
    const token = await this.getActionToken('conversion.click');
    await this.httpClient.post(
      '/v1/conversion/click',
      payload ?? {},
      { headers: { 'X-Action-Token': token } },
    );
  }

  async recordPaymentAnalyticsEvent(
    payload: PaymentAnalyticsEventCreateRequest,
  ): Promise<PaymentAnalyticsEventCreateResponse | null> {
    try {
      return await this.httpClient.post<PaymentAnalyticsEventCreateResponse>(
        '/v1/analytics/events',
        payload,
      );
    }
    catch (error) {
      console.warn('recordPaymentAnalyticsEvent error:', error);
      return null;
    }
  }

  async confirmSubscription(payload: {
    email: string;
    paymentData: { paymentIntentId: string };
    meta?: string;
    planId?: string;
  }): Promise<SubscriptionConfirmResponse> {
    const token = await this.getActionToken('signup.subscription.confirm');
    const response = await this.httpClient.post<SubscriptionConfirmResponse>(
      '/v1/signup/subscription/confirm',
      payload,
      { headers: { 'X-Action-Token': token } },
    );

    // Update tokens in store
    if (response.access) {
      runInAction(() => {
        this.accessToken = response.access.accessToken;
        this.refreshToken = response.access.refreshToken;
        this.saveTokens();
      });

      // Fetch user profile and subscription status after successful confirmation
      await this.getMe();
      await this.getSubscriptionStatus();
    }

    return response;
  }
}

export const apiStore = new ApiStore();
